import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { XClose } from "@untitledui/icons";
import { useChatAI } from "@/components/application/ai-chat/chat-ai-context";

const HELP_POPOVER_SESSION_KEY = "askEve.helpPopoverDismissed";
const IDLE_MS = 5000;
const MOVE_DEBOUNCE_MS = 250;

/**
 * FAB in basso a destra sul viewport per aprire/chiudere Ask Eve.
 * Dopo inattività mostra un popover chiudibile sopra al FAB.
 */
export function AskEveFab() {
    const { t } = useTranslation("navigation");
    const { toggleAskEvePanel, isAskEvePanelOpen } = useChatAI();
    const [showHelpPopover, setShowHelpPopover] = useState(false);
    const showHelpPopoverRef = useRef(false);
    useEffect(() => {
        showHelpPopoverRef.current = showHelpPopover;
    }, [showHelpPopover]);

    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const moveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isPanelOpenRef = useRef(isAskEvePanelOpen);
    useEffect(() => {
        isPanelOpenRef.current = isAskEvePanelOpen;
    }, [isAskEvePanelOpen]);

    const dismissPopoverForSession = useCallback(() => {
        try {
            sessionStorage.setItem(HELP_POPOVER_SESSION_KEY, "1");
        } catch {
            /* ignore */
        }
        setShowHelpPopover(false);
    }, []);

    const clearIdleTimer = useCallback(() => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
    }, []);

    const scheduleIdleShow = useCallback(() => {
        clearIdleTimer();
        try {
            if (sessionStorage.getItem(HELP_POPOVER_SESSION_KEY) === "1") return;
        } catch {
            /* ignore */
        }
        if (isAskEvePanelOpen) return;
        idleTimerRef.current = setTimeout(() => {
            try {
                if (sessionStorage.getItem(HELP_POPOVER_SESSION_KEY) === "1") return;
            } catch {
                /* ignore */
            }
            if (!isPanelOpenRef.current) setShowHelpPopover(true);
        }, IDLE_MS);
    }, [clearIdleTimer, isAskEvePanelOpen]);

    /** Chiusura manuale (X): nasconde il popover e riavvia il ciclo idle 5s (senza bloccare la sessione). */
    const closeHelpPopoverAndRestartIdle = useCallback(() => {
        setShowHelpPopover(false);
        scheduleIdleShow();
    }, [scheduleIdleShow]);

    useEffect(() => {
        if (isAskEvePanelOpen) {
            setShowHelpPopover(false);
            clearIdleTimer();
        }
    }, [isAskEvePanelOpen, clearIdleTimer]);

    useEffect(() => {
        let dismissed = false;
        try {
            dismissed = sessionStorage.getItem(HELP_POPOVER_SESSION_KEY) === "1";
        } catch {
            /* ignore */
        }
        if (dismissed || isAskEvePanelOpen) return;

        scheduleIdleShow();

        const bumpIdle = () => {
            // Con popover già visibile: niente chiusura automatica; l'utente chiude solo con la X.
            if (showHelpPopoverRef.current) return;
            scheduleIdleShow();
        };

        const onPointerDown = () => bumpIdle();
        const onKeyDown = () => bumpIdle();
        const onScroll = () => bumpIdle();
        const onMouseMove = () => {
            if (moveTimerRef.current) return;
            moveTimerRef.current = setTimeout(() => {
                moveTimerRef.current = null;
                bumpIdle();
            }, MOVE_DEBOUNCE_MS);
        };

        window.addEventListener("pointerdown", onPointerDown, true);
        window.addEventListener("keydown", onKeyDown, true);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("mousemove", onMouseMove, true);

        return () => {
            clearIdleTimer();
            if (moveTimerRef.current) clearTimeout(moveTimerRef.current);
            window.removeEventListener("pointerdown", onPointerDown, true);
            window.removeEventListener("keydown", onKeyDown, true);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("mousemove", onMouseMove, true);
        };
    }, [clearIdleTimer, isAskEvePanelOpen, scheduleIdleShow]);

    const handleFabClick = () => {
        dismissPopoverForSession();
        toggleAskEvePanel();
    };

    /** Con pannello aperto il FAB coprirebbe il pulsante invio della chat: non renderizzare. */
    if (isAskEvePanelOpen) {
        return null;
    }

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-2">
            {showHelpPopover ? (
                <div
                    role="dialog"
                    aria-label={t("askEveHelpPopover")}
                    className="relative max-w-[min(280px,calc(100vw-3rem))] rounded-xl border border-secondary bg-primary px-3 py-2.5 text-center shadow-lg ring-1 ring-black/5 dark:ring-white/10"
                >
                    <p className="pr-6 text-sm font-medium text-primary">{t("askEveHelpPopover")}</p>
                    <button
                        type="button"
                        className="absolute right-1.5 top-1.5 rounded-md p-1 text-tertiary hover:bg-secondary hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                        aria-label={t("askEveHelpDismiss")}
                        onClick={() => closeHelpPopoverAndRestartIdle()}
                    >
                        <XClose className="size-4" aria-hidden />
                    </button>
                </div>
            ) : null}
            <button
                type="button"
                className="flex size-14 shrink-0 items-center justify-center rounded-full border border-white/35 bg-brand-solid shadow-[0_10px_40px_-8px_rgba(0,0,0,0.45),0_0_0_1px_rgba(255,255,255,0.2)] ring-2 ring-white/40 transition hover:bg-brand-solid_hover hover:shadow-[0_14px_44px_-6px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.28)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary dark:border-white/25 dark:ring-white/30 dark:shadow-[0_12px_48px_-8px_rgba(224,120,86,0.55),0_0_0_1px_rgba(255,255,255,0.12)] dark:hover:shadow-[0_16px_52px_-6px_rgba(224,120,86,0.6),0_0_0_1px_rgba(255,255,255,0.18)]"
                onClick={handleFabClick}
                aria-label={t("askEve")}
                aria-expanded={false}
            >
                <img src="/icons/ICO-ask-eve.svg" alt="" className="size-8" aria-hidden />
            </button>
        </div>
    );
}
